import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Mail, ArrowRight, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import API from '@/lib/api';
import { FOOTER_TEXT_DEFAULTS, footerText } from '@/lib/footerText';

export const FOOTER_DEFAULTS = {
  contact_email: 'hello@ernestetmarguerite.com',
  ...FOOTER_TEXT_DEFAULTS,
  nav_title: 'Navigation', legal_title: 'Informations légales',
  nav_links: [
    { label: 'Accueil', url: '/' }, { label: 'Notre histoire', url: '/a-propos' }, { label: 'Le marché', url: '/produits' }, { label: 'Où livrons-nous ?', url: '/livraison' },
    { label: 'Les producteurs', url: '/producteurs' }, { label: 'Les plats tout prêts', url: '/plats-tout-prets' },
    { label: 'Le journal', url: '/journal' }, { label: 'Les listes de courses', url: '/inspirations' }, { label: 'Nous contacter', url: '/contact' },
  ],
  legal_links: [
    { label: 'Mentions légales', url: '/mentions-legales' }, { label: 'Conditions Générales de Vente', url: '/conditions-generales-de-vente' },
    { label: 'Politique de confidentialité (RGPD)', url: '/politique-confidentialite' }, { label: 'Politique de cookies', url: '/politique-cookies' },
  ],
};

const slug = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function FooterLink({ link, prefix }) {
  const cls = 'font-sans text-sm hover:text-white transition-colors';
  const external = /^https?:\/\//i.test(link.url || '');
  return external
    ? <a href={link.url} target="_blank" rel="noreferrer" className={cls} data-testid={`${prefix}-${slug(link.label)}`}>{link.label}</a>
    : <Link to={link.url || '/'} className={cls} data-testid={`${prefix}-${slug(link.label)}`}>{link.label}</Link>;
}

function NewsletterBlock({ footer }) {
  const [nlEmail, setNlEmail] = useState('');
  const [nlPostal, setNlPostal] = useState('');
  const [nlSending, setNlSending] = useState(false);
  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!nlEmail) return;
    setNlSending(true);
    try {
      const res = await API.post('/newsletter/subscribe', { email: nlEmail, postal_code: nlPostal || null });
      toast.success(res.data.message);
      setNlEmail(''); setNlPostal('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setNlSending(false); }
  };
  return (
    <div className="bg-white/10 rounded-2xl p-8 mb-12" data-testid="newsletter-section">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-5 w-5 text-forest" />
            <h3 className="font-serif text-xl font-bold text-white">{footer?.newsletter_title || 'Restez informé'}</h3>
          </div>
          <p className="font-sans text-sm text-white/60">{footer?.newsletter_subtitle || 'Recevez un email dès qu\'Ernest & Marguerite arrivent dans votre commune. Indiquez votre code postal pour être prévenu en priorité.'}</p>
        </div>
        <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <Input value={nlEmail} onChange={e => setNlEmail(e.target.value)} type="email" required placeholder="votre@email.be" className="bg-white/10 border-white/20 text-white placeholder:text-white/40 min-w-[200px]" data-testid="newsletter-email" />
          <Input value={nlPostal} onChange={e => setNlPostal(e.target.value)} placeholder="Code postal" className="bg-white/10 border-white/20 text-white placeholder:text-white/40 w-32" data-testid="newsletter-postal" />
          <Button type="submit" disabled={nlSending} className="bg-brick text-white rounded-full px-6 hover:bg-brick/90 whitespace-nowrap" data-testid="newsletter-submit">
            {nlSending ? '...' : 'M\'inscrire'} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function Footer() {
  const { user } = useAuth();
  const { brand, footer } = useSiteSettings();
  const f = { ...FOOTER_DEFAULTS, ...(footer || {}) };
  const siteName = brand?.site_name || 'Ernest & Marguerite';
  const showPublicNav = !user || user.role === 'client';
  const navLinks = Array.isArray(f.nav_links) && f.nav_links.length ? f.nav_links : FOOTER_DEFAULTS.nav_links;
  const legalLinks = Array.isArray(f.legal_links) ? f.legal_links : FOOTER_DEFAULTS.legal_links;
  const reopenCookies = () => {
    localStorage.removeItem('terroir_cookie_consent');
    localStorage.removeItem('terroir_cookie_consent_v2');
    window.dispatchEvent(new Event('reopen-cookie-banner'));
  };

  return (
    <footer className="bg-stone-text text-white/80 py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {f.show_newsletter !== false && <NewsletterBlock footer={f} />}

        <div className={`grid grid-cols-1 ${showPublicNav ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-12`}>
          <div>
            {brand?.logo_url ? (
              <img src={brand.logo_url} alt={siteName} className="h-12 w-auto object-contain mb-4 brightness-0 invert opacity-90" />
            ) : (
              <h3 className="font-serif text-2xl font-bold text-white mb-4">{siteName}</h3>
            )}
            <p className="font-sans text-sm leading-relaxed" data-testid="footer-description">{f.company_description || 'Le marché en ligne des produits locaux belges. Découvrez le meilleur de nos provinces.'}</p>
            {f.address_line && <p className="font-sans text-sm mt-3 flex items-start gap-2" data-testid="footer-address"><MapPin className="h-4 w-4 mt-0.5 shrink-0" /> {f.address_line}</p>}
          </div>
          {showPublicNav && (
            <div>
              <h4 className="font-sans font-medium text-white mb-4">{f.nav_title}</h4>
              <div className="flex flex-col gap-2" data-testid="footer-nav-links">
                {navLinks.map((l, i) => <FooterLink key={`${l.url}-${i}`} link={l} prefix="footer-nav" />)}
              </div>
            </div>
          )}
          <div>
            <h4 className="font-sans font-medium text-white mb-4">{f.legal_title}</h4>
            <div className="flex flex-col gap-2" data-testid="footer-legal-links">
              {legalLinks.map((l, i) => <FooterLink key={`${l.url}-${i}`} link={l} prefix="footer-legal" />)}
              {f.show_cookie_button !== false && (
                <button type="button" onClick={reopenCookies} className="font-sans text-sm hover:text-white transition-colors text-left" data-testid="footer-manage-cookies">
                  Gérer mes cookies
                </button>
              )}
              {f.contact_email && <a href={`mailto:${f.contact_email}`} className="font-sans text-sm hover:text-white transition-colors" data-testid="footer-contact-email">{f.contact_email}</a>}
              {f.contact_phone && <a href={`tel:${f.contact_phone.replace(/\s+/g, '')}`} className="font-sans text-sm hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-contact-phone"><Phone className="h-3.5 w-3.5" /> {f.contact_phone}</a>}
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-sans text-xs text-white/40" data-testid="footer-copyright">{footerText(f.copyright || FOOTER_DEFAULTS.copyright, f, siteName)}</p>
          {f.show_trust_badges !== false && (
            <div className="flex items-center gap-4 flex-wrap" data-testid="trust-badges">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-sans text-white/60 bg-white/5 border border-white/10 rounded-full px-3 py-1.5" data-testid="trust-badge-stripe">
                <Lock className="h-3 w-3 text-green-400" /> Paiement sécurisé Stripe
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-sans text-white/60 bg-white/5 border border-white/10 rounded-full px-3 py-1.5" data-testid="trust-badge-rgpd">
                <ShieldCheck className="h-3 w-3 text-blue-300" /> RGPD-compliant
              </span>
            </div>
          )}
          <span className="sr-only" data-testid="build-version" aria-hidden="true">Build {process.env.REACT_APP_BUILD_ID || 'dev'}</span>
        </div>
      </div>
    </footer>
  );
}
